import { useQuery } from '@tanstack/react-query';
import { compact, concat, get, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { IComponent } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { fetchComponents } from '../components';
import { fetchThemes } from '../themes';
import { mapAssemblyComponents, mapViewAssemblyComponents } from './helpers';

export const useGetAssemblyComponents = (
  assemblyId: string,
  enabled = true,
  parentAssemblyId?: string,
) => {
  const assemblyIds = compact([assemblyId, parentAssemblyId]);

  return useQuery({
    queryKey: queryKeys.assemblyComponents.all(
      !isEmpty(assemblyIds)
        ? `assembly_id.list=${assemblyIds.join('|')}`
        : undefined,
    ),
    queryFn: () => fetchAssemblyComponents(assemblyId, parentAssemblyId),
    staleTime: Infinity,
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const fetchAssemblyComponents = async (
  assemblyId: string,
  parentAssemblyId?: string,
  componentIncludes?: string[],
) => {
  const assemblyIds = compact([assemblyId, parentAssemblyId]);

  const query = !isEmpty(assemblyIds)
    ? `assembly_id.list=${assemblyIds.join('|')}`
    : '';

  const url = `/assemblyComponents${generateQuery(
    ['assemblies', 'layoutScreens', 'screenSections', 'components'],
    ['pageSize=0', query],
  )}`;

  const assemblyComponentsResponse = await requestCCApi({ url: url });

  const componentIDs = get(
    assemblyComponentsResponse,
    'includes.components.data',
    [],
  ).map((component: IComponent) => component.id);

  if (!isEmpty(componentIDs)) {
    const components = await fetchComponents(
      [`id.list=${componentIDs.join('|')}`, 'pageSize=0'],
      false,
      componentIncludes,
    );

    assemblyComponentsResponse.includes.components.data = components;
  }

  return mapAssemblyComponents(assemblyComponentsResponse, assemblyId);
};

export const useViewAssemblyComponents = (
  filters: string[] = [],
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeys.viewAssembliesWithComponents.all(filters),
    queryFn: () => fetchViewAssemblyComponents(filters),
    staleTime: Infinity,
    enabled,
  });
};

const fetchViewAssemblyComponents = async (filters: string[] = []) => {
  const url = `/viewAssembliesWithComponents${generateQuery(
    ['layouts'],
    concat(filters, ['pageSize=0']),
  )}`;

  const [response, themes] = await Promise.all([
    requestCCApi({ url: url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.themes.all(),
      queryFn: () => fetchThemes(),
      staleTime: Infinity,
    }),
  ]);

  return mapViewAssemblyComponents(response, themes);
};

export const fetchAssemblyComponentsForExpiry = async (assemblyId: number) => {
  const url = `/viewAssembliesWithComponents${generateQuery(
    [],
    [`id=${assemblyId}`, 'omitCovered=true', 'pageSize=0'],
  )}`;

  const response = await requestCCApi({ url });

  return response.data && response.data.length > 0 ? response.data[0] : null;
};
